import React, { useEffect, useState, useCallback } from 'react';
import { TextField, Button, Box, Typography, Grid } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InputAdornment from '@mui/material/InputAdornment';
import Slider from '@mui/material/Slider';
import useNotification from '../hooks/useNotification';
import Notification from './Notification';
import FileDropOverlay from './utils/FileDropOverlay';

const ArrayMaker = () => {
  const [inputText, setInputText] = useState('');
  const [customDelimiter, setCustomDelimiter] = useState('');
  const [selectedCommonDelimiters, setSelectedCommonDelimiters] = useState([',', '|']);
  const [selectedOutputDelimiter, setSelectedOutputDelimiter] = useState(',');
  const [isCustomDelimiterButtonActive, setIsCustomDelimiterButtonActive] = useState(false);
  const [isCustomOutputDelimiterButtonActive, setIsCustomOutputDelimiterButtonActive] = useState(false);
  const [customOutputDelimiter, setCustomOutputDelimiter] = useState('');
  const [removeDuplicates, setRemoveDuplicates] = useState(true);
  const [outputQuoteType, setOutputQuoteType] = useState('single');
  const [outputArrayString, setOutputArrayString] = useState('');
  const [bracketType, setBracketType] = useState('square');
  const [caseChange, setCaseChange] = useState('none');
  const [trimOption, setTrimOption] = useState(false);
  const [minItemLength, setMinItemLength] = useState(0);
  const [maxItemLength, setMaxItemLength] = useState(0);
  const [trimRange, setTrimRange] = useState([0, 0]);
  const [hasSliderBeenTouched, setHasSliderBeenTouched] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [itemCount, setItemCount] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [sortOption, setSortOption] = useState('none');

  const bracketTypeOptions = ['square', 'round', 'none'];
  const outputQuoteTypeOptions = ['single', 'double', 'none'];
  const caseChangeOptions = ['lowercase', 'uppercase', 'capitalize', 'none'];

  const commonDelimiterOptions = [
    { label: 'Comma', value: ',' },
    { label: 'Pipe', value: '|' },
    { label: 'Colon', value: ':' },
    { label: 'Space', value: ' ' },
    { label: 'Tab', value: '\t' },
    { label: 'Underscore', value: '_' },
  ];

  const outputDelimiterOptions = [
    { label: 'Comma', value: ',' },
    { label: 'Pipe', value: '|' },
    { label: 'New Line', value: `\n` },
    { label: 'Tab', value: `\t` },
  ];

  const sortOptions = [
    { label: 'None', value: 'none' },
    { label: 'Ascending', value: 'ascending' },
    { label: 'Descending', value: 'descending' }
  ];

  const { snackbarOpen, snackbarMessage, handleCopy, showNotification, handleCloseSnackbar } = useNotification();

  // Converts the input text into a formatted array string based on selected options.
  const convertToArray = useCallback(() => {
    const allActiveDelimiters = new Set(selectedCommonDelimiters);
    // Add custom delimiter only if its button is active AND the custom delimiter input has a value
    if (isCustomDelimiterButtonActive && customDelimiter) {
      allActiveDelimiters.add(customDelimiter);
    }

    const activeDelimitersArray = [...allActiveDelimiters];

    // Always split the input into lines first
    const lines = inputText.split(/\r?\n/);
    let items = [];

    if (activeDelimitersArray.length > 0) {
      // Escape special regex characters in each delimiter to use them in a RegExp
      const escapedDelimiters = activeDelimitersArray.map(d =>
        d.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      );
      const regex = new RegExp(`(${escapedDelimiters.join('|')})`, 'g');

      lines.forEach(line => {
        // Split each line by the combined delimiters
        const lineItems = line.split(regex);
        const filteredLineItems = lineItems.filter(
          item => item !== '' && !allActiveDelimiters.has(item)
        );
        items = items.concat(filteredLineItems);
      });
    } else {
      // If no delimiters are selected, each line is treated as a single item
      items = lines;
    }

    items = items.map(item => item.trim()).filter(item => item !== '');

    if (caseChange !== 'none') {
      items = items.map(item => {
        switch (caseChange) {
          case 'lowercase':
            return item.toLowerCase();
          case 'uppercase':
            return item.toUpperCase();
          case 'capitalize':
            return item.split(' ').map(word =>
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            ).join(' ');
          default:
            return item;
        }
      });
    }

    if (removeDuplicates) {
      items = [...new Set(items)];
    };

    // Calculate min/max lengths for slider (always calculate)
    const currentLengths = items.map(item => item.length);
    const calculatedMinLength = currentLengths.length > 0 ? Math.min(...currentLengths) : 0;
    const calculatedMaxLength = currentLengths.length > 0 ? Math.max(...currentLengths) : 0;

    setMinItemLength(calculatedMinLength);
    setMaxItemLength(calculatedMaxLength);

    // --- Apply Trim Logic based on trimOption and trimRange ---
    if (trimOption) {
      // Apply slice based on the trimRange [start, end]
      items = items.map(item => {
        const currentItemLength = item.length;
        const [sliderStart, sliderEnd] = trimRange;

        // Calculate effective start and end for this specific item, clamping them to item's bounds
        let effectiveStart = Math.max(0, sliderStart);
        let effectiveEnd = Math.min(currentItemLength, sliderEnd);

        // This prevents items from disappearing if the trim range is outside their bounds.
        if (currentItemLength > 0 && effectiveEnd <= effectiveStart) {
          return item;
        }

        if (effectiveStart >= currentItemLength) {
          return item;
        }

        return item.slice(effectiveStart, effectiveEnd);
      });
    }

    if (removeDuplicates) {
      items = [...new Set(items)];
    };

    if (sortOption === 'ascending') {
      items.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'accent', numeric: true }));
    } else if (sortOption === 'descending') {
      items.sort((a, b) => b.localeCompare(a, undefined, { sensitivity: 'accent', numeric: true }));
    }

    const quoteChar = outputQuoteType === 'single'
      ? "'"
      : outputQuoteType === 'double'
        ? '"'
        : '';
    let openBracket = '';
    let closeBracket = '';
    if (bracketType === 'square') {
      openBracket = '[';
      closeBracket = ']';
    } else if (bracketType === 'round') {
      openBracket = '(';
      closeBracket = ')';
    };

    let outputDelimiter = selectedOutputDelimiter;
    if (isCustomOutputDelimiterButtonActive && customOutputDelimiter) {
      outputDelimiter = customOutputDelimiter;
    }
    const formattedItems = items.map(item => `${quoteChar}${item}${quoteChar}`);
    const isNewLineDelimiter = /\n/.test(outputDelimiter) && (openBracket || closeBracket);
    const innerContent = formattedItems.join(outputDelimiter);

    const paddedContent = isNewLineDelimiter
      ? `${outputDelimiter}${innerContent}${outputDelimiter}`
      : innerContent;

    setOutputArrayString(`${openBracket}${paddedContent}${closeBracket}`);

    const delimiterRegex = new RegExp(`[${[...allActiveDelimiters].map(d => d.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('')}]`, 'g');
    const noDelimitersText = inputText.replace(delimiterRegex, '').replace(/\r?\n/g, '');
    setCharCount(noDelimitersText.length);
    setItemCount(items.length);

  }, [
    inputText,
    customDelimiter,
    isCustomDelimiterButtonActive,
    selectedCommonDelimiters,
    removeDuplicates,
    outputQuoteType,
    bracketType,
    caseChange,
    trimOption,
    trimRange,
    selectedOutputDelimiter,
    isCustomOutputDelimiterButtonActive,
    customOutputDelimiter,
    sortOption,
  ]);

  // Trigger conversion whenever relevant state changes
  useEffect(() => {
    convertToArray();
  }, [convertToArray]);

  useEffect(() => {
    if (trimOption && !hasSliderBeenTouched) {
      // Initialize to full range [0, maxItemLength] when trim is enabled and not touched
      setTrimRange([0, maxItemLength]);
    } else if (!trimOption) {
      // When trim is off, reset to full range for next time 'Yes' is selected
      setTrimRange([0, maxItemLength]);
      setHasSliderBeenTouched(false); // Reset touched flag when trim is turned off
    }
  }, [maxItemLength, trimOption, hasSliderBeenTouched]);

  const handleTrimSliderChange = useCallback((event, newValue) => {
    setTrimRange(newValue); // newValue will be an array [start, end]
    setHasSliderBeenTouched(true); // Mark that the user has interacted with the slider
  }, []);

  const handleCommonDelimiterToggle = (value) => {
    setSelectedCommonDelimiters(prev =>
      prev.includes(value) ? prev.filter(d => d !== value) : [...prev, value]
    );
  };

  const handleOutputDelimiterToggle = (value) => {
    setSelectedOutputDelimiter(value);
    setIsCustomOutputDelimiterButtonActive(false);
  };

  // Activates/deactivates the custom delimiter input field and clears its value if deactivated.
  const handleCustomDelimiterButtonToggle = () => {
    setIsCustomDelimiterButtonActive(prev => !prev);
    if (isCustomDelimiterButtonActive) {
      setCustomDelimiter('');
    }
  };

  const handleCustomOutputDelimiterButtonToggle = () => {
    setIsCustomOutputDelimiterButtonActive(prev => !prev);
    if (!isCustomOutputDelimiterButtonActive) {
      setSelectedOutputDelimiter('');
      setCustomOutputDelimiter('');

    }
  };

  const handleCustomOutputDelimiterInput = (e) => {
    const newValue = e.target.value[0] || '';
    setCustomOutputDelimiter(newValue);

    if (newValue.length > 0) {
      setIsCustomOutputDelimiterButtonActive(true);
      setSelectedOutputDelimiter('');
    }
  };

  const handleBracketTypeChange = () => {
    setBracketType((prev) => {
      const currentIndex = bracketTypeOptions.indexOf(prev);
      const nextIndex = (currentIndex + 1) % bracketTypeOptions.length;
      return bracketTypeOptions[nextIndex];
    });
  };

  const handleOutputQuoteTypeChange = () => {
    setOutputQuoteType((prev) => {
      const currentIndex = outputQuoteTypeOptions.indexOf(prev);
      const nextIndex = (currentIndex + 1) % outputQuoteTypeOptions.length;
      return outputQuoteTypeOptions[nextIndex];
    });
  };

  const handleSortOptionCycle = () => {
    setSortOption((prev) => {
      const currentIndex = sortOptions.findIndex(option => option.value === prev);
      const nextIndex = (currentIndex + 1) % sortOptions.length;
      return sortOptions[nextIndex].value;
    });
  };

  const getBracketButtonLabel = () => {
    switch (bracketType) {
      case 'square':
        return 'Square [ ]';
      case 'round':
        return 'Round ( )';
      case 'none':
        return 'None';
      default:
        return 'Square [ ]';
    }
  };

  const getQuoteType = () => {
    switch (outputQuoteType) {
      case 'single':
        return 'single';
      case 'double':
        return 'double';
      case 'none':
        return 'none';
      default:
        return 'single';
    }
  };

  const handleFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  };

  const handleFileDragLeave = (e) => {
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleFileDrop = (e) => {
    e.preventDefault(); // Prevent default browser behavior (e.g., opening file)
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      const reader = new FileReader();

      reader.onload = (event) => {
        setInputText(event.target.result);
        showNotification(`File "${file.name}" loaded successfully!`);
      };

      reader.onerror = () => {
        showNotification(`Error reading file "${file.name}".`);
      };

      reader.readAsText(file);
    } else {
      showNotification('No file dropped or invalid drop operation.');
    }
  };

  return (
    <Grid container spacing={4} direction="column">
      {/* Grid container for responsive layout */}
      <Grid item xs={12}>
        {/* Main container for all option rows, using vertical flexbox */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5, // A bit more space between rows for clarity
            marginBottom: 1,
          }}
        >
          {/* Row 1: Select Delimiters */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography
              variant="button"
              sx={{
                width: { xs: '100%', sm: '170px' }, // Full width on mobile, fixed on desktop
                flexShrink: 0,
              }}
            >
              Input Delimiters:
            </Typography>
            {/* Container for the controls */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {commonDelimiterOptions.map((option) => (
                <Button
                  key={option.value}
                  onClick={() => handleCommonDelimiterToggle(option.value)}
                  variant={selectedCommonDelimiters.includes(option.value) ? 'contained' : 'outlined'}
                  color="primary"
                  size="small"
                >
                  {option.label} ({option.value === ' ' ? ' ' : option.value === '\t' ? '\\t' : option.value})
                </Button>
              ))}
              <Button
                onClick={handleCustomDelimiterButtonToggle}
                variant={isCustomDelimiterButtonActive ? 'contained' : 'outlined'}
                color="primary"
                size="small"
              >
                Custom
              </Button>
              {isCustomDelimiterButtonActive && (
                <TextField
                  label="e.g. ; / \ #"
                  variant="outlined"
                  size="small"
                  value={customDelimiter}
                  onChange={(e) => setCustomDelimiter(e.target.value[0])}
                  sx={{
                    width: '80px',
                    alignContent: 'center',
                    '& .MuiInputBase-root': {
                      height: '30px', // Match MUI small button height
                      padding: '0 15px',
                    },
                    '& .MuiInputBase-input': {
                      padding: 0,
                      fontSize: '0.8125rem', // Match button font
                      lineHeight: '30px', // Align vertically
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.75rem',
                    },
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Row 2: Remove Duplicates */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography
              variant="button"
              sx={{ width: { xs: '100%', sm: '170px' }, flexShrink: 0 }}
            >
              Remove Duplicates:
            </Typography>
            <Button
              onClick={() => setRemoveDuplicates((prev) => !prev)}
              variant={removeDuplicates ? 'contained' : 'outlined'}
              color="primary"
              size="small"
            >
              {removeDuplicates ? 'Yes' : 'No'}
            </Button>
          </Box>

          {/* Row 3: Output Quote Type */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography
              variant="button"
              sx={{ width: { xs: '100%', sm: '170px' }, flexShrink: 0 }}
            >
              Output Quote Type:
            </Typography>
            <Button
              onClick={handleOutputQuoteTypeChange}
              variant='contained'
              color="primary"
              size="small"
            >
              {getQuoteType()}
            </Button>
          </Box>

          {/* Row 4: Bracket Type */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography
              variant="button"
              sx={{ width: { xs: '100%', sm: '170px' }, flexShrink: 0 }}
            >
              Bracket Type:
            </Typography>
            <Button
              onClick={handleBracketTypeChange}
              variant='contained'
              color="primary"
              size="small"
            >
              {getBracketButtonLabel()}
            </Button>
          </Box>
          {/* Row 5: Convert case */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography
              variant="button"
              sx={{ width: { xs: '100%', sm: '170px' }, flexShrink: 0 }}
            >
              Case change:
            </Typography>
            <Button
              onClick={() => setCaseChange((prev) => {
                const currentIndex = caseChangeOptions.indexOf(prev);
                const nextIndex = (currentIndex + 1) % caseChangeOptions.length;
                return caseChangeOptions[nextIndex];
              })}
              variant='contained'
              color="primary"
              size="small"
            >
              {caseChange}
            </Button>
          </Box>

          {/* Row 6: Trim Output */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography
              variant="button"
              sx={{ width: { xs: '100%', sm: '170px' }, flexShrink: 0 }}
            >
              Trim Output:
            </Typography>
            <Button
              onClick={() => {
                setTrimOption((prev) => !prev)
                setHasSliderBeenTouched(false);
              }}
              variant={trimOption ? 'contained' : 'outlined'}
              color="primary"
              size="small"
            >
              {trimOption ? 'Yes' : 'No'}
            </Button>
            <Box sx={{
              width: { xs: '80%', sm: '200px' },
              ml: { xs: 0, sm: 2 },
              opacity: trimOption ? 1 : 0,
              pointerEvents: trimOption ? 'auto' : 'none',
              transition: 'opacity 0.3s ease-in-out',
              display: 'flex',
              flexDirection: 'column',
              alignContent: 'center',
            }}>
              <Slider
                key={`${maxItemLength}-${minItemLength}`}
                value={trimRange}
                onChange={handleTrimSliderChange}
                min={0}
                max={maxItemLength}
                step={1}
                valueLabelDisplay="auto"
                disabled={maxItemLength === 0}
                minDistance={minItemLength}
                getAriaLabel={() => 'Trim range'}
                getAriaValueText={(value) => `Trim from ${value[0]} to ${value[1]}`}
              />
            </Box>
          </Box>

          {/* Row 7: Sort Output */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography
              variant="button"
              sx={{ width: { xs: '100%', sm: '170px' }, flexShrink: 0 }}
            >
              Sort Output:
            </Typography>
            <Button
              onClick={handleSortOptionCycle}
              variant='contained'
              color="primary"
              size="small"
              sx={{ textTransform: 'uppercase' }}
            >
              {sortOption}
            </Button>
          </Box>

          {/* Row 8: Select Output Delimiter */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography
              variant="button"
              sx={{
                width: { xs: '100%', sm: '170px' }, // Full width on mobile, fixed on desktop
                flexShrink: 0,
              }}
            >
              Output Delimiter:
            </Typography>
            {/* Container for the controls */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {outputDelimiterOptions.map((option) => (
                <Button
                  key={option.value}
                  onClick={() => handleOutputDelimiterToggle(option.value)}
                  variant={selectedOutputDelimiter === option.value && !isCustomOutputDelimiterButtonActive ? 'contained' : 'outlined'}
                  color="primary"
                  size="small"
                >
                  {option.label} ({option.value === '\n' ? '\\n' : option.value === '\t' ? '\\t' : option.value})
                </Button>
              ))}
              <Button
                onClick={handleCustomOutputDelimiterButtonToggle}
                variant={isCustomOutputDelimiterButtonActive ? 'contained' : 'outlined'}
                color="primary"
                size="small"
              >
                Custom
              </Button>
              <TextField
                label="e.g. | / \"
                variant="outlined"
                size="small"
                value={customOutputDelimiter}
                onChange={handleCustomOutputDelimiterInput}
                sx={{
                  width: '80px',
                  alignContent: 'center',
                  '& .MuiInputBase-root': {
                    height: '30px', // Match MUI small button height
                    padding: '0 15px',
                  },
                  '& .MuiInputBase-input': {
                    padding: 0,
                    fontSize: '0.8125rem', // Match button font
                    lineHeight: '30px', // Align vertically
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.75rem',
                  },
                }}
              />
            </Box>
          </Box>

        </Box>
      </Grid>

      {/* Input Section */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2, // Spacing within input section
            marginBottom: 1, // Space below input before output
          }}
        >
          {/* Wrapper Box for TextField and Overlay */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 'auto',
            }}
            onDragOver={handleFileDragOver}
            onDragLeave={handleFileDragLeave}
            onDrop={handleFileDrop}
          >
            {isDragging && <FileDropOverlay />}
            {/* Text field for user input */}
            <TextField
              label={
                inputText.length
                  ? `Your Input — ${charCount} characters`
                  : !isDragging && 'Drag & drop file or type/paste text'
              }
              multiline
              rows={8}
              variant="outlined"
              fullWidth
              value={inputText}
              onChange={(e) => setInputText(e.target.value.replace(/['"(){}[\]]/g, ''))}
              placeholder={
                !isDragging && `Enter text here\ne.g:\n\napple, banana, cherry\norange|grape|kiwi`
              }
              sx={{
                borderRadius: 1,
                '& .MuiInputBase-input': { paddingRight: '45px' }
              }}
              slotProps={{
                input: {
                  endAdornment: inputText.length > 0 && (
                    <InputAdornment position="end" sx={{ position: 'absolute', top: 15, right: 22, zIndex: 5 }}>
                      <IconButton onClick={() => handleCopy(inputText, 'Input copied to clipboard!')} size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        </Box>
      </Grid>

      {/* Output Section */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2, // Spacing within output section
          }}
        >
          {/* Text field to display the resulting array string */}
          <TextField
            label={inputText.length ? `Your ${itemCount} item array` : "Output Array"}
            multiline
            rows={8}
            variant="outlined"
            fullWidth
            value={inputText.length ? outputArrayString : ''}
            sx={{
              borderRadius: 1,
              '& .MuiInputBase-input': { paddingRight: '45px' }
            }}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: inputText.length > 0 && (
                  <InputAdornment position="end" sx={{ position: 'absolute', top: 15, right: 22, zIndex: 5 }}>
                    <IconButton
                      onClick={() => handleCopy(outputArrayString, 'Output copied to clipboard!')}
                      size="small"
                      disabled={!inputText.length}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <Notification
          open={snackbarOpen}
          message={snackbarMessage}
          handleClose={handleCloseSnackbar}
        />
      </Grid>
    </Grid>
  );
};

export default ArrayMaker;